import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import style from "./DataExtractStyle";
import {
  FiDownload,
  FiUpload,
  FiFileText,
  FiChevronLeft,
  FiChevronRight,
  FiLoader,
} from "react-icons/fi";
import {
  importData,
  getPaginatedData,
  exportDataToExcel,
} from "../../Services/dbservice";
import { AuthContext } from "../../Context/AuthContext";

const tableConfig = {
  chats: {
    label: "Clientes",
    headers: [
      "Contato",
      "Nome",
      "Status Lead",
      "Msgs Trocadas",
      "Data de Criação",
    ],
    formatRow: (item) => [
      item.id,
      item.clientName || "Não informado",
      item.leadStatus || "Não definido",
      item.messageCount,
      new Date(item.dateCreated).toLocaleDateString("pt-BR"),
    ],
  },
  sales: {
    label: "Vendas",
    headers: ["ID Venda", "ID Cliente", "Qtd. Produtos", "Valor Total", "Data"],
    formatRow: (item) => [
      item.id,
      item.clientId,
      item.productsCount,
      (item.totalAmount || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
      item.dateCreated
        ? new Date(item.dateCreated).toLocaleDateString("pt-BR")
        : "N/A",
    ],
  },
  shots: {
    label: "Disparos",
    headers: ["ID Disparo", "Nome", "Modelo", "Clientes", "Data de Envio"],
    formatRow: (item) => [
      item.id,
      item.name,
      item.modelName || "N/A",
      item.clientsQtt || 0,
      item.sendShotDate
        ? new Date(item.sendShotDate).toLocaleDateString("pt-BR")
        : "Não enviado",
    ],
  },
};

const ITEMS_PER_PAGE = 15;

export default function DataExtract() {
  const [selectedTable, setSelectedTable] = useState("chats");
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const { credentials } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(
    async (page) => {
      if (!credentials?.accessToken) return;
      setLoading(true);
      try {
        const data = await getPaginatedData(
          selectedTable,
          page,
          ITEMS_PER_PAGE,
          credentials.accessToken
        );

        setTableData(data.items);
        setTotalPages(Math.ceil(data.totalCount / ITEMS_PER_PAGE));
        setCurrentPage(page);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setTableData([]);
        setTotalPages(1);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    },
    [selectedTable, credentials?.accessToken]
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleTableChange = (e) => {
    setSelectedTable(e.target.value);
  };

  const handleDownloadExcel = async () => {
    try {
      alert(
        `Iniciando o download da tabela de ${tableConfig[selectedTable].label}. Isso pode levar um momento.`
      );
      const response = await exportDataToExcel(
        selectedTable,
        credentials.accessToken
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const fileName = `relatorio-${selectedTable}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro no download:", error);
      alert("Não foi possível baixar o arquivo. Tente novamente.");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    setLoading(true);
    try {
      const response = await importData(
        file,
        selectedTable,
        credentials.accessToken
      );
      alert(response.message || "Importação concluída com sucesso!");
      fetchData(1);
    } catch (error) {
      console.error("Erro na importação:", error);
      alert(error.message || "Ocorreu um erro durante a importação.");
    } finally {
      setLoading(false);
      event.target.value = null;
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) fetchData(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) fetchData(currentPage + 1);
  };

  const currentConfig = tableConfig[selectedTable];

  return (
    <div style={style.container}>
      <div style={style.header}>
        <h1 style={style.title}>Extração de Relatórios</h1>
      </div>

      <div style={style.panel}>
        <div style={style.filterGroup}>
          <FiFileText style={style.selectIcon} />
          <select
            style={style.selectInput}
            value={selectedTable}
            onChange={handleTableChange}
          >
            {Object.keys(tableConfig).map((key) => (
              <option key={key} value={key}>
                {tableConfig[key].label}
              </option>
            ))}
          </select>
        </div>
        <div style={style.actionsGroup}>
          <button onClick={handleDownloadExcel} style={style.downloadButton}>
            <FiDownload style={{ marginRight: "8px" }} /> Baixar Excel
          </button>
        </div>
      </div>

      <div style={style.tableContainer}>
        <div
          style={{
            ...style.tableHeader,
            gridTemplateColumns: `repeat(${currentConfig.headers.length}, 1fr)`,
          }}
        >
          {currentConfig.headers.map((header, index) => (
            <span key={index} style={style.tableHeaderCell}>
              {header}
            </span>
          ))}
        </div>

        <div style={style.tableBody}>
          {loading ? (
            <div style={style.messageCenter}>
              <FiLoader className="spin-animation" size={24} /> Carregando...
            </div>
          ) : tableData.length > 0 ? (
            tableData.map((row, rowIndex) => (
              <div
                key={rowIndex}
                style={{
                  ...style.tableRow,
                  gridTemplateColumns: `repeat(${currentConfig.headers.length}, 1fr)`,
                }}
                className="table-row-hover"
              >
                {currentConfig.formatRow(row).map((cell, cellIndex) => (
                  <span key={cellIndex} style={style.tableCell}>
                    {cell}
                  </span>
                ))}
              </div>
            ))
          ) : (
            <div style={style.messageCenter}>Nenhum dado para exibir.</div>
          )}
        </div>

        {totalPages > 1 && (
          <div style={style.paginationContainer}>
            <button
              style={{
                ...style.paginationButton,
                ...(currentPage === 1 ? style.disabledButton : {}),
              }}
              onClick={handlePrevPage}
              disabled={currentPage === 1 || loading}
            >
              <FiChevronLeft /> Anterior
            </button>
            <span style={style.paginationView}>
              Página {currentPage} de {totalPages}
            </span>
            <button
              style={{
                ...style.paginationButton,
                ...(currentPage === totalPages ? style.disabledButton : {}),
              }}
              onClick={handleNextPage}
              disabled={currentPage === totalPages || loading}
            >
              Próxima <FiChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
